import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { reportService } from '../../services/reportService';
import { fleetService } from '../../services/fleetService';
import DriverPerformanceTable from '../../components/reports/DriverPerformanceTable';
import DriverPerformanceChart from '../../components/drivers/DriverPerformanceChart';
import ReportExportOptions from '../../components/reports/ReportExportOptions';
import { Calendar, ChevronDown, FilterX } from 'lucide-react';

const DriverReportsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [drivers, setDrivers] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [fleets, setFleets] = useState([]);
    const [activeTab, setActiveTab] = useState('table');
    const [filters, setFilters] = useState({
        fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
        start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        metric: 'total_trips'
    });

    const isAdmin = user.user_type === 'admin';

    // 指標選項
    const metricOptions = [
        { value: 'total_trips', label: '總接單數' },
        { value: 'acceptance_rate', label: '接單率 (%)' },
        { value: 'cancellation_rate', label: '取消率 (%)' },
        { value: 'online_hours', label: '在線時數' },
        { value: 'rating', label: '評分' },
        { value: 'total_earnings', label: '總收入' }
    ];

    useEffect(() => {
        fetchDriverPerformance();
        if (isAdmin) {
            fetchFleets();
        }
    }, [filters.fleet_id, filters.start_date, filters.end_date]);

    const fetchDriverPerformance = async () => {
        setIsLoading(true);
        try {
            // 獲取司機績效數據
            const params = {
                fleet_id: filters.fleet_id || undefined,
                start_date: filters.start_date,
                end_date: filters.end_date
            };

            const { data, trends } = await reportService.getDriverPerformance(params);
            setDrivers(data);

            // 處理圖表數據
            processChartData(trends);
        } catch (error) {
            console.error('Error fetching driver performance:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    // 處理圖表數據
    const processChartData = (trends) => {
        // 將趨勢數據處理為圖表所需格式
        if (!trends) return;

        // 假設 trends 是一個包含每個司機按日期排列的趨勢數據
        // 例如: { driver_id_1: [{date: '2023-01-01', total_trips: 10, ...}, ...], ... }

        // 選擇前10名司機進行顯示
        const topDrivers = Object.keys(trends).slice(0, 10);

        // 獲取所有日期
        const dates = [];
        topDrivers.forEach(driverId => {
            trends[driverId].forEach(entry => {
                if (!dates.includes(entry.date)) {
                    dates.push(entry.date);
                }
            });
        });
        dates.sort();

        // 按日期重組數據
        const chartData = dates.map(date => {
            const dataPoint = { date };

            topDrivers.forEach(driverId => {
                const driverEntry = trends[driverId].find(e => e.date === date);
                if (driverEntry) {
                    const driverName = drivers.find(d => d.driver_id.toString() === driverId)?.full_name || driverId;
                    dataPoint[driverName] = driverEntry[filters.metric];
                } else {
                    const driverName = drivers.find(d => d.driver_id.toString() === driverId)?.full_name || driverId;
                    dataPoint[driverName] = 0;
                }
            });

            return dataPoint;
        });

        setChartData(chartData);
    };

    const handleFleetChange = (e) => {
        setFilters(prev => ({
            ...prev,
            fleet_id: e.target.value
        }));
    };

    const handleDateChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMetricChange = (e) => {
        setFilters(prev => ({
            ...prev,
            metric: e.target.value
        }));
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleResetFilters = () => {
        setFilters({
            fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
            start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            metric: 'total_trips'
        });
    };

    const handleExportReport = (format) => {
        try {
            reportService.exportDriverReport({
                fleet_id: filters.fleet_id || undefined,
                start_date: filters.start_date,
                end_date: filters.end_date,
                format
            });
        } catch (error) {
            console.error('Error exporting report:', error);
        }
    };

    const handleDriverClick = (driver) => {
        navigate(`/drivers/${driver.driver_id}`);
    };

    // 為圖表準備指標配置
    const getChartMetrics = () => {
        // 從 drivers 中獲取前5名司機的名稱和數據
        const topDrivers = drivers
            .sort((a, b) => b[filters.metric] - a[filters.metric])
            .slice(0, 5);

        return topDrivers.map((driver, index) => ({
            key: driver.full_name,
            name: driver.full_name,
            color: `hsl(${index * 60}, 70%, 50%)`
        }));
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">司機績效報表</h1>
                <ReportExportOptions onExport={handleExportReport} />
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-6 text-gray-500">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span>報表期間: {filters.start_date} 至 {filters.end_date}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {isAdmin && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                選擇車隊
                            </label>
                            <select
                                value={filters.fleet_id}
                                onChange={handleFleetChange}
                                className="w-full border border-gray-300 rounded-md p-2"
                            >
                                <option value="">所有車隊</option>
                                {fleets.map(fleet => (
                                    <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                        {fleet.fleet_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            開始日期
                        </label>
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => handleDateChange('start_date', e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            結束日期
                        </label>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => handleDateChange('end_date', e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            圖表指標
                        </label>
                        <select
                            value={filters.metric}
                            onChange={handleMetricChange}
                            className="w-full border border-gray-300 rounded-md p-2"
                        >
                            {metricOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleResetFilters}
                        className="flex items-center text-sm text-blue-500 hover:text-blue-700"
                    >
                        <FilterX className="w-4 h-4 mr-1" />
                        重置篩選
                    </button>
                </div>
            </div>

            {/* 報表標籤切換 */}
            <div className="flex border-b border-gray-200">
                <button
                    className={`px-6 py-3 border-b-2 text-sm font-medium ${activeTab === 'table'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    onClick={() => handleTabChange('table')}
                >
                    表格視圖
                </button>
                <button
                    className={`px-6 py-3 border-b-2 text-sm font-medium ${activeTab === 'chart'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    onClick={() => handleTabChange('chart')}
                >
                    圖表視圖
                </button>
            </div>

            {/* 報表內容 */}
            <div>
                {activeTab === 'table' ? (
                    <DriverPerformanceTable
                        data={drivers}
                        loading={isLoading}
                        onExport={handleExportReport}
                        onRowClick={handleDriverClick}
                    />
                ) : (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h3 className="font-bold mb-4">
                            前5名司機 - {metricOptions.find(o => o.value === filters.metric)?.label || filters.metric}
                        </h3>
                        <DriverPerformanceChart
                            data={chartData}
                            type="line"
                            metrics={getChartMetrics()}
                            xAxisKey="date"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DriverReportsPage;